import { Spinner } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  QuoteStatusEnum,
  QuoteStatusToDealStageMappingForm,
  QuoteStatusToDealStageMappingFormSchema,
  QuoteStatusToDealStageMappingType,
} from '@monetize/types/app';
import { FC, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { BsArrowRight } from 'react-icons/bs';
import {
  useGetAttioDealStages,
  useSaveQuoteStatusToDealStageMappings,
} from '../../../../../../api/settingsService';
import {
  MAccordion,
  MAccordionCustomButton,
  MFlex,
  MIcon,
  MText,
} from '../../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../../components/Monetize/MSettingAccordionItem';
import { MappingForm } from './components/MappingForm';

interface QuoteStatusToDealStageMappingProps {
  existingMappings: QuoteStatusToDealStageMappingType[];
}

export const QuoteStatusToDealStageMapping: FC<
  QuoteStatusToDealStageMappingProps
> = ({ existingMappings }) => {
  const { data: attioDealStages, isLoading: isDealStagesLoading } =
    useGetAttioDealStages();

  // Create initial mappings for all quote statuses
  const allQuoteStatuses = Object.values(QuoteStatusEnum);

  const initialMappings = useMemo(() => {
    return allQuoteStatuses.map((status) => {
      const existingMapping = existingMappings.find(
        (m) => m.mnQuoteStatus === status,
      );
      return {
        mnQuoteStatus: status,
        attioDealStageId: existingMapping?.attioDealStageId || '',
      };
    });
  }, [existingMappings, allQuoteStatuses]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<QuoteStatusToDealStageMappingForm>({
    resolver: zodResolver(QuoteStatusToDealStageMappingFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      mappings: initialMappings,
    },
  });

  const { mutate: saveMappings, isPending: isSaving } =
    useSaveQuoteStatusToDealStageMappings({
      meta: { showErrorToast: true },
      onSuccess: (data) => {
        // Reset form with the new values to clear dirty state
        const updatedMappings = allQuoteStatuses.map((status) => {
          const savedMapping = data.quoteStatusToDealStageMappings.find(
            (m) => m.mnQuoteStatus === status,
          );
          return {
            mnQuoteStatus: status,
            attioDealStageId: savedMapping?.attioDealStageId || '',
          };
        });
        reset({ mappings: updatedMappings });
      },
    });

  const { fields } = useFieldArray({
    name: 'mappings',
    control,
  });

  // Convert QuoteStatusEnum to options
  const quoteStatuses = Object.values(QuoteStatusEnum).map((status) => ({
    label:
      status.replace(/_/g, ' ').charAt(0) +
      status.replace(/_/g, ' ').slice(1).toLowerCase(),
    value: status,
  }));

  const dealStages = useMemo(() => {
    return (
      attioDealStages?.map((stage) => ({
        label: stage.value,
        value: stage.id,
      })) || []
    );
  }, [attioDealStages]);

  const onSubmit = (data: QuoteStatusToDealStageMappingForm) => {
    // Filter out mappings with empty deal stage IDs before saving
    const validMappings = data.mappings.filter(
      (mapping) => mapping.attioDealStageId && mapping.attioDealStageId !== '',
    );
    saveMappings(validMappings);
  };

  return (
    <MFlex direction="column" gap={4}>
      {isDealStagesLoading ? (
        <MFlex justify="center">
          <Spinner />
        </MFlex>
      ) : (
        <MAccordion collapsible w="100%">
          <MSettingAccordionItem
            renderAccordionButton={({ isExpanded }) => {
              return (
                <MAccordionCustomButton
                  isExpanded={isExpanded}
                  label=""
                  customLabel={
                    <MFlex align="center" gap={2} ml={1}>
                      <MText
                        fontWeight="semibold"
                        fontSize="lg"
                        textTransform="capitalize"
                      >
                        Quote Status
                      </MText>
                      <MIcon as={BsArrowRight} />
                      <MText
                        fontWeight="semibold"
                        fontSize="lg"
                        textTransform="capitalize"
                      >
                        Deal Stage
                      </MText>
                    </MFlex>
                  }
                ></MAccordionCustomButton>
              );
            }}
          >
            <MText>
              To change the deal stage in Attio when the quote status changes in
              MonetizeNow, you can set up the mapping here.
            </MText>
            <MappingForm
              fields={fields}
              control={control}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              isDirty={isDirty}
              isSaving={isSaving}
              errors={errors}
              quoteStatuses={quoteStatuses}
              dealStages={dealStages}
            />
          </MSettingAccordionItem>
        </MAccordion>
      )}
    </MFlex>
  );
};
